import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { scrollToHashWhenReady } from "../../utils/scrollToHash";
import { investorApi } from "../../api/api";
import GradientButton from "../common/GradiantButton";
import FadeInOnScroll from "../common/FadeInOnScroll";
import PropertyList from "../Header/Property/PropertyList";
import HomeHero from "../Home/HomeHero";
import HomeServices from "../Home/HomeServices";
import HomeValues from "../Home/HomeValues";
import HomePanIndia from "../Home/HomePanIndia";
import HomeFinalCta from "../Home/HomeFinalCta";
import {
  Typography,
  Box,
  TextField,
  Paper,
  Autocomplete,
  Fab,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import HomeLegalExperts from "../Header/Home/HomeLegalExperts";

const HomeSearch = () => {
  const themeBanner = useSelector(
    (state) => state.themeValue?.logourl?.banner_image
  );
  const [location, setLocation] = useState("");
  const [keyword, setKeyword] = useState("");
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState("");
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [carouselUrls, setCarouselUrls] = useState([]);

  const theme = useSelector((data) => data.themeValue.colors);
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");

  useEffect(() => {
    if (routeLocation.hash !== "#services") return undefined;
    return scrollToHashWhenReady("#services");
  }, [routeLocation.pathname, routeLocation.hash]);

  const normalizeBannerUrl = (url) =>
    url && String(url).trim() ? String(url).trim() : null;

  // Prefer home_banners carousel; else single banner_image; else gradient (empty slides)
  useEffect(() => {
    let cancelled = false;
    const fetchHomeBanners = async () => {
      try {
        const res = await investorApi.getHomeBanners();
        const list = Array.isArray(res.data) ? res.data : [];
        const urls = list
          .map((b) => normalizeBannerUrl(b?.image_url))
          .filter(Boolean)
          .slice(0, 4);
        if (!cancelled) setCarouselUrls(urls);
      } catch (err) {
        console.error("Error fetching home banners:", err);
        if (!cancelled) setCarouselUrls([]);
      }
    };
    fetchHomeBanners();
    return () => {
      cancelled = true;
    };
  }, []);

  const bannerSlides = useMemo(() => {
    if (carouselUrls.length > 0) return carouselUrls;
    const single = normalizeBannerUrl(themeBanner);
    return single ? [single] : [];
  }, [carouselUrls, themeBanner]);

  const formatText = (text = "") =>
    text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await investorApi.getAllPropertiesAndProjects();
        setProperties(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Error fetching property data:", err);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const locationCity = useMemo(() => {
    const cityMap = new Map();
    properties.forEach((item) => {
      if (item?.city) {
        const trimmedCity = item.city.trim();
        const normalizedCity = trimmedCity.toLowerCase();
        if (!cityMap.has(normalizedCity)) {
          cityMap.set(normalizedCity, formatText(trimmedCity));
        }
      }
    });
    return Array.from(cityMap.values());
  }, [properties]);

  const addressOptions = useMemo(() => {
    const addressMap = new Map();
    properties.forEach((p) => {
      if (p?.address && p?.city) {
        const trimmedAddress = p.address.trim();
        const trimmedCity = p.city.trim();
        const normalizedKey =
          trimmedAddress.toLowerCase() + "_" + trimmedCity.toLowerCase();
        if (!addressMap.has(normalizedKey)) {
          addressMap.set(normalizedKey, {
            label: `${formatText(trimmedAddress)}, ${formatText(trimmedCity)}`,
            address: formatText(trimmedAddress),
            city: formatText(trimmedCity),
          });
        }
      }
    });
    return Array.from(addressMap.values());
  }, [properties]);

  const handleSearch = (selectedCity = "") => {
    setError("");
    const finalLocation = selectedCity || location;
    const selectedAddress = keyword.split(",")[0]?.trim() || "";
    const selectedCityFromKeyword = keyword.split(",")[1]?.trim() || "";

    navigate("/search-properties", {
      state: {
        address: selectedAddress,
        city: finalLocation || selectedCityFromKeyword,
      },
    });
  };

  return (
    <>
      <FadeInOnScroll variant="fade-in">
        <HomeHero bannerSlides={bannerSlides} />
      </FadeInOnScroll>

      {/* Existing property search — kept below hero */}
      <FadeInOnScroll delay={80}>
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          mt: { xs: -3, sm: -4 },
          mb: { xs: 2, sm: 3 },
          position: "relative",
          zIndex: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: { xs: 2, sm: 2.5 },
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            width: "100%",
            boxSizing: "border-box",
            backgroundColor: theme.screen_background || "#fff",
          }}
        >
          <Autocomplete
            freeSolo
            options={locationCity}
            value={location}
            sx={{ width: { sm: "22%" }, display: { xs: "none", sm: "block" } }}
            onChange={(e, newValue) => setLocation(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="City"
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                  },
                }}
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={addressOptions}
            inputValue={keyword}
            onInputChange={(event, newInputValue) => setKeyword(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={isMobile ? "Search by City..." : "Search by Area..."}
                variant="outlined"
                size="small"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "5px" },
                  backgroundColor: "white",
                }}
              />
            )}
            sx={{
              flex: 2,
              width: { xs: "100%" },
              display: { xs: "none", sm: "block" },
            }}
          />

          <Autocomplete
            freeSolo
            options={locationCity}
            value={location}
            onChange={(e, newValue) => {
              setLocation(newValue || "");
              if (newValue) handleSearch(newValue);
            }}
            onInputChange={(e, newValue) => setLocation(newValue || "")}
            sx={{ width: "100%", display: { xs: "block", sm: "none" } }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by City..."
                variant="outlined"
                size="small"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSearch(location || e.target.value);
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "5px",
                    backgroundColor: "#fff",
                  },
                }}
              />
            )}
          />

          <GradientButton
            text="Search"
            onClick={() => handleSearch()}
            width={{ xs: "100%", sm: "140px" }}
            height={42}
            sx={{ display: { xs: "none", sm: "flex" } }}
          />
        </Paper>
        {error && (
          <Typography sx={{ color: "error.main", mt: 1, textAlign: "center" }}>
            {error}
          </Typography>
        )}
      </Box>
      </FadeInOnScroll>

      <FadeInOnScroll>
        <PropertyList variant="home" />
      </FadeInOnScroll>
      <FadeInOnScroll>
        <HomeServices />
      </FadeInOnScroll>
      <FadeInOnScroll>
        <HomeValues />
      </FadeInOnScroll>
      <FadeInOnScroll variant="fade-up" delay={40}>
        <HomePanIndia />
      </FadeInOnScroll>
      <FadeInOnScroll>
        <HomeFinalCta />
      </FadeInOnScroll>
      <FadeInOnScroll>
        <HomeLegalExperts />
      </FadeInOnScroll>

      {showScrollButton && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            bottom: 30,
            right: 30,
            zIndex: 2000,
            background: `linear-gradient(90deg, ${theme.primary_gradient_start_color}, ${theme.primary_gradient_end_color})`,
            color: "#fff",
            "&:hover": { opacity: 0.9 },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </>
  );
};

export default HomeSearch;
